"use client";

import { Icon } from "../Icon";

export interface SearchTriggerProps {
  /**
   * Placeholder text to show in the trigger
   * @default "Search..."
   */
  placeholder?: string;
  /**
   * Handler for when the trigger is clicked
   */
  onClick?: () => void;
  /**
   * Whether to show the keyboard shortcut hint
   * @default true
   */
  showShortcut?: boolean;
  /**
   * Visual variant of the search trigger
   * - `default`: Standard input styling (clean, neutral)
   * - `elevated`: Premium styling for dashboard global search (muted bg, subtle shadow, enhanced focus)
   * @default "default"
   */
  variant?: "default" | "elevated";
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * SearchTrigger
 *
 * A prominent search input-style trigger button for opening the command palette.
 * Designed to be wide and center-aligned in the TopNav on desktop.
 *
 * @example
 * ```tsx
 * <SearchTrigger
 *   onClick={() => setIsOpen(true)}
 *   placeholder="Search sites, devices, contacts..."
 * />
 * ```
 */
export function SearchTrigger({
  placeholder = "Search...",
  onClick,
  showShortcut = true,
  variant = "default",
  className = "",
}: SearchTriggerProps) {
  const isMac = typeof navigator !== "undefined" && navigator.platform.includes("Mac");

  // Variant styles
  const variantStyles = {
    default: `
      bg-background-input border border-border-input
      hover:border-border-focus hover:bg-background-surface
    `,
    elevated: `
      bg-background-muted/50 border border-border-subtle/50
      focus-within:bg-background-body focus-within:border-border-focus focus-within:shadow-md
      backdrop-blur-sm
    `,
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        flex items-center gap-3 w-full px-4 py-2.5 rounded-lg
        transition-all duration-200 cursor-text
        ${variantStyles[variant]}
        ${className}
      `}
      aria-label="Open search"
    >
      {/* Search Icon */}
      <Icon
        name="MagnifyingGlass"
        size={18}
        className={`flex-shrink-0 transition-colors ${variant === "elevated" ? "text-text-secondary" : "text-text-secondary"}`}
      />

      {/* Placeholder Text */}
      <span
        className={`flex-1 text-left text-sm transition-colors ${
          variant === "elevated" ? "text-text-secondary font-normal" : "text-text-disabled"
        }`}
      >
        {placeholder}
      </span>

      {/* Keyboard Shortcut */}
      {showShortcut && (
        <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
          <kbd
            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
              variant === "elevated"
                ? "bg-background-surface/80 border border-border-muted text-text-secondary"
                : "bg-background-muted border border-border-muted"
            }`}
          >
            {isMac ? "⌘" : "Ctrl"}
          </kbd>
          <kbd
            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
              variant === "elevated"
                ? "bg-background-surface/80 border border-border-muted text-text-secondary"
                : "bg-background-muted border border-border-muted"
            }`}
          >
            K
          </kbd>
        </div>
      )}
    </button>
  );
}
